import { trackErrorReport } from '../../../../utils/errorTracker';

/** Detect iOS (iPhone/iPad) for audio hints (e.g. Ring/Silent switch). */
export const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Returns a MediaRecorder-compatible audio mime type for the current browser.
 * Safari does not support audio/webm; it uses audio/mp4. Use this to avoid
 * "NotSupportedError" when creating MediaRecorder in Safari.
 * @returns {string|undefined} Preferred mime type or undefined for browser default
 */
export const getSupportedAudioMimeType = () => {
  if (typeof window === 'undefined' || !window.MediaRecorder || !MediaRecorder.isTypeSupported) {
    return undefined;
  }
  if (MediaRecorder.isTypeSupported('audio/webm')) {
    return 'audio/webm';
  }
  if (MediaRecorder.isTypeSupported('audio/mp4')) {
    return 'audio/mp4';
  }
  return undefined;
};

/**
 * Speaking-only MIME negotiation. Prefer explicit codec for safety.
 * Order: audio/webm;codecs=opus -> audio/webm -> audio/mp4 -> undefined (browser default).
 * NOTE: getSupportedAudioMimeType() (above) is kept for Pronunciation's useAudioRecording hook.
 * @returns {string|undefined}
 */
export const getSpeakingAudioMimeType = () => {
  if (typeof window === 'undefined' || !window.MediaRecorder || !MediaRecorder.isTypeSupported) {
    return undefined;
  }
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return undefined;
};

/**
 * Map a MediaRecorder mime type to upload filename, content-type, and extension.
 * Strips codec params (e.g. ";codecs=opus") before matching.
 * Fallback: webm with a console.warn.
 * @param {string|undefined|null} mimeType
 * @returns {{ extension: string, contentType: string, filename: string }}
 */
export const getAudioFileMeta = (mimeType) => {
  const baseType = (mimeType || '').split(';')[0].trim().toLowerCase();
  const map = {
    'audio/webm':  { extension: 'webm', contentType: 'audio/webm' },
    'audio/mp4':   { extension: 'm4a',  contentType: 'audio/mp4'  },
    'audio/ogg':   { extension: 'ogg',  contentType: 'audio/ogg'  },
    'audio/wav':   { extension: 'wav',  contentType: 'audio/wav'  },
    'audio/x-wav': { extension: 'wav',  contentType: 'audio/wav'  },
    'audio/mpeg':  { extension: 'mp3',  contentType: 'audio/mpeg' },
  };
  const matched = map[baseType];
  if (matched) {
    return { ...matched, filename: `recording.${matched.extension}` };
  }
  console.warn(`⚠️ getAudioFileMeta: unrecognized mime "${mimeType}", defaulting to webm`);
  return { extension: 'webm', contentType: 'audio/webm', filename: 'recording.webm' };
};

/**
 * Mint a UUID-like recording id. Uses crypto.randomUUID if available, else falls back.
 * @returns {string}
 */
export const createRecordingId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `rec-${crypto.randomUUID()}`;
    }
  } catch (_) { /* fallthrough */ }
  return `rec-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

const safeJsonStringify = (value) => {
  try { return JSON.stringify(value); } catch (_) { return '[unserializable]'; }
};

/**
 * Emit a media diagnostic event. Goes to console + error tracker for BE correlation.
 * Event types used in Speaking flow: 'recording.start' | 'recording.stop' | 'upload.dispatch'.
 * Full payload is JSON-stringified into the tracker `stack` field (free-form string).
 * Never throws — logging must not break the recording pipeline.
 * @param {string} event
 * @param {object} payload  must include `recordingId`; other fields per event spec
 */
export const logMediaDiagnostic = (event, payload = {}) => {
  const enriched = { event, timestamp: new Date().toISOString(), ...payload };
  try {
    if (typeof console !== 'undefined' && console.log) {
      console.log(`📊 MediaDiagnostic[${event}]`, enriched);
    }
  } catch (_) { /* ignore */ }
  try {
    trackErrorReport({
      errorType: 'media-diagnostic',
      message: `media-diagnostic:${event}`,
      code: event,
      stack: safeJsonStringify(enriched),
      page: payload.page || 'SpeakingPractice',
      action: event,
      practiceSessionId: payload.practiceSessionId || null,
      recordingId: payload.recordingId || null,
    });
  } catch (_) { /* never crash recording on diag failure */ }
};

/**
 * @deprecated for Speaking — kept ONLY for Pronunciation's useAudioRecording hook.
 * Speaking now ships the original blob (webm/mp4) and lets Whisper handle it.
 * TODO(follow-up PR): migrate Pronunciation off client-side WAV conversion, then delete.
 *
 * Converts a recorded audio Blob (webm, mp4, etc.) to WAV using Web Audio API.
 * Needed for Safari (audio/mp4) and for backends that expect WAV.
 * Uses webkitAudioContext for Safari compatibility.
 * @param {Blob} audioBlob - Recorded blob (e.g. audio/webm or audio/mp4)
 * @returns {Promise<Blob>} WAV blob, or original blob if conversion fails
 */
export const convertBlobToWav = (audioBlob) => {
  return new Promise((resolve) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      resolve(audioBlob);
      return;
    }
    const audioContext = new AudioContextClass();
    const fileReader = new FileReader();
    fileReader.onloadend = () => {
      audioContext.decodeAudioData(fileReader.result)
        .then((audioBuffer) => {
          const wav = audioBufferToWav(audioBuffer);
          resolve(new Blob([wav], { type: 'audio/wav' }));
        })
        .catch((err) => {
          console.warn('convertBlobToWav failed, sending original blob:', err);
          resolve(audioBlob);
        });
    };
    fileReader.onerror = () => {
      trackErrorReport({
        errorType: 'media',
        message: 'Failed to read recorded audio blob',
        code: 'file-reader-error',
        page: 'SpeakingPractice',
        action: 'convert-audio',
      });
      resolve(audioBlob);
    };
    fileReader.readAsArrayBuffer(audioBlob);
  });
};

/**
 * @deprecated for Speaking — kept ONLY for Pronunciation's useAudioRecording hook (via convertToWav).
 * TODO(follow-up PR): migrate Pronunciation off client-side WAV conversion, then delete.
 */
export const audioBufferToWav = (buffer) => {
  const length = buffer.length;
  const sampleRate = buffer.sampleRate;
  const numberOfChannels = buffer.numberOfChannels;
  const bytesPerSample = 2;
  const blockAlign = numberOfChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = length * blockAlign;
  const bufferSize = 44 + dataSize;
  const arrayBuffer = new ArrayBuffer(bufferSize);
  const view = new DataView(arrayBuffer);
  
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, bufferSize - 8, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);
  
  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }
  
  return arrayBuffer;
};

export const getUniversalLearningId = () => {
  const savedId = localStorage.getItem('universal-learning-id');
  if (savedId) return savedId;
  
  try {
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      const id = userData['universal-learning-id'] || userData.universalLearningId;
      if (id) {
        localStorage.setItem('universal-learning-id', id);
        return id;
      }
    }
  } catch (error) {
    console.error('Error parsing userData:', error);
  }
  
  return null;
};

